import { useMemo, useState } from "react";
import { CalendarClock, MessageCircle, RotateCcw } from "lucide-react";
import Reveal from "./Reveal";
import { openTriage } from "../data/office";

/**
 * Teste informativo "Já posso me aposentar?" — usa apenas as regras gerais de idade.
 * Não substitui a análise do CNIS: regras de transição, períodos especiais e
 * outros detalhes só aparecem na análise individual.
 */

const chip = (on) =>
  `rounded-full border px-4 py-2 text-sm transition-colors ${
    on
      ? "border-gold-500 bg-gold-500 font-semibold text-navy-950"
      : "border-navy-600 text-mist hover:border-gold-500/60 hover:text-cream"
  }`;

const numInput =
  "w-28 rounded-lg border border-navy-600 bg-navy-900 px-4 py-3 text-sm text-cream outline-none focus:border-gold-500";

function evaluate({ sexo, idade, rural, anosRural, anosContrib, antesReforma }) {
  const f = sexo === "Mulher";
  const out = [];

  if (rural !== "Não") {
    const idadeMin = f ? 55 : 60;
    const ok = idade >= idadeMin && anosRural >= 15;
    out.push({
      ok,
      title: "Aposentadoria rural por idade",
      text: ok
        ? `Pelas informações, você pode já preencher os requisitos gerais (${idadeMin} anos de idade e 15 anos de atividade rural). O ponto decisivo é a prova do trabalho rural.`
        : `Regra geral: ${idadeMin} anos de idade e 15 anos de atividade rural.${
            idade < idadeMin ? ` Faltam cerca de ${idadeMin - idade} ano(s) de idade.` : ""
          }${anosRural < 15 ? ` Faltam cerca de ${15 - anosRural} ano(s) de atividade rural.` : ""}`,
    });
  }

  const idadeUrb = f ? 62 : 65;
  const contribMin = f || antesReforma ? 15 : 20;
  const total = anosContrib + (rural !== "Não" ? anosRural : 0);
  const okUrb = idade >= idadeUrb && anosContrib >= contribMin;
  out.push({
    ok: okUrb,
    title: "Aposentadoria por idade (urbana)",
    text: okUrb
      ? `Pelas informações, você pode já preencher os requisitos gerais (${idadeUrb} anos de idade e ${contribMin} anos de contribuição).`
      : `Regra geral: ${idadeUrb} anos de idade e ${contribMin} anos de contribuição.${
          idade < idadeUrb ? ` Faltam cerca de ${idadeUrb - idade} ano(s) de idade.` : ""
        }${anosContrib < contribMin ? ` Faltam cerca de ${contribMin - anosContrib} ano(s) de contribuição.` : ""}`,
  });

  if (rural !== "Não" && !okUrb && idade >= idadeUrb && total >= contribMin) {
    out.push({
      ok: true,
      title: "Aposentadoria híbrida",
      text: "Somando o tempo rural com o urbano, você pode alcançar o tempo mínimo exigido. Vale analisar essa possibilidade.",
    });
  }

  if (antesReforma && anosContrib >= (f ? 28 : 33)) {
    out.push({
      ok: null,
      title: "Regras de transição",
      text: "Como você já contribuía antes da Reforma de 2019 e tem bastante tempo de contribuição, pode haver uma regra de transição mais vantajosa. Isso exige cálculo individual.",
    });
  }
  return out;
}

export default function RetirementCheck({ compact = false }) {
  const [sexo, setSexo] = useState("");
  const [idade, setIdade] = useState("");
  const [rural, setRural] = useState("");
  const [anosRural, setAnosRural] = useState("");
  const [anosContrib, setAnosContrib] = useState("");
  const [antes, setAntes] = useState("");
  const [show, setShow] = useState(false);

  const ready = sexo && idade && rural && antes && anosContrib !== "" && (rural === "Não" || anosRural !== "");
  const result = useMemo(
    () =>
      show && ready
        ? evaluate({
            sexo,
            idade: Number(idade),
            rural,
            anosRural: Number(anosRural) || 0,
            anosContrib: Number(anosContrib) || 0,
            antesReforma: antes === "Sim",
          })
        : null,
    [show, ready, sexo, idade, rural, anosRural, anosContrib, antes]
  );

  const reset = () => {
    setSexo("");
    setIdade("");
    setRural("");
    setAnosRural("");
    setAnosContrib("");
    setAntes("");
    setShow(false);
  };

  const talk = () =>
    openTriage({
      title: "Planejamento de Aposentadoria",
      area: "previdenciario",
      prefill:
        `Fiz o teste do site: ${sexo}, ${idade} anos, ${anosContrib} ano(s) de contribuição urbana` +
        (rural !== "Não" ? `, cerca de ${anosRural} ano(s) de trabalho rural` : "") +
        `, contribuía antes de nov/2019: ${antes}.`,
    });

  return (
    <section
      id="aposentadoria"
      data-testid="retirement-check"
      className={compact ? "mx-auto max-w-5xl px-5 py-16 sm:px-8" : "relative bg-navy-950 py-24 sm:py-32"}
    >
      <div className={compact ? "" : "mx-auto max-w-5xl px-5 sm:px-8"}>
        <Reveal>
          <p className="font-mono-meta flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-gold-400">
            <CalendarClock className="h-4 w-4" /> Teste rápido · 1 minuto
          </p>
          <h2 className="font-display mt-4 text-4xl font-medium leading-tight text-cream sm:text-5xl">
            Já posso me <em className="gold-text">aposentar?</em>
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-mist">
            Responda e veja uma orientação inicial pelas regras gerais da aposentadoria por idade.
          </p>
        </Reveal>

        <div className="mt-10 rounded-2xl border border-gold-500/25 bg-navy-800 p-6 sm:p-9">
          <div className="grid grid-cols-1 gap-7 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-mist">Você é</p>
              <div className="flex gap-2">
                {["Mulher", "Homem"].map((o) => (
                  <button key={o} type="button" className={chip(sexo === o)} onClick={() => setSexo(o)}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-mist" htmlFor="rc-idade">
                Sua idade
              </label>
              <input id="rc-idade" type="number" min="16" max="100" inputMode="numeric" value={idade} onChange={(e) => setIdade(e.target.value)} className={numInput} placeholder="anos" />
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-mist">Trabalhou na roça (atividade rural)?</p>
              <div className="flex flex-wrap gap-2">
                {["Não", "Sim"].map((o) => (
                  <button key={o} type="button" className={chip(rural === o)} onClick={() => setRural(o)}>
                    {o}
                  </button>
                ))}
              </div>
              {rural === "Sim" && (
                <div className="mt-3 flex items-center gap-3 text-sm text-mist">
                  <input type="number" min="0" max="80" inputMode="numeric" value={anosRural} onChange={(e) => setAnosRural(e.target.value)} className={numInput} placeholder="anos" aria-label="Anos de atividade rural" />
                  anos de atividade rural (aprox.)
                </div>
              )}
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-mist" htmlFor="rc-contrib">
                Anos de contribuição ao INSS
              </label>
              <div className="flex items-center gap-3 text-sm text-mist">
                <input id="rc-contrib" type="number" min="0" max="60" inputMode="numeric" value={anosContrib} onChange={(e) => setAnosContrib(e.target.value)} className={numInput} placeholder="anos" />
                carteira assinada ou carnê (aprox.)
              </div>
            </div>
            <div className="md:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-mist">Já contribuía para o INSS antes de novembro de 2019?</p>
              <div className="flex flex-wrap gap-2">
                {["Sim", "Não"].map((o) => (
                  <button key={o} type="button" className={chip(antes === o)} onClick={() => setAntes(o)}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              type="button"
              data-testid="rc-submit"
              disabled={!ready}
              onClick={() => setShow(true)}
              className="rounded-full bg-gold-500 px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-950 transition-all hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Ver resultado
            </button>
            {show && (
              <button type="button" onClick={reset} className="inline-flex items-center gap-2 text-sm text-mist hover:text-gold-400">
                <RotateCcw className="h-4 w-4" /> Refazer
              </button>
            )}
          </div>

          {result && (
            <div data-testid="rc-result" className="mt-8 space-y-4 border-t border-navy-700 pt-8">
              {result.map((r) => (
                <div
                  key={r.title}
                  className={`rounded-xl border p-5 ${
                    r.ok === true ? "border-gold-500/60 bg-gold-500/10" : "border-navy-600 bg-navy-900/50"
                  }`}
                >
                  <p className="font-display text-xl font-semibold text-cream">
                    {r.ok === true ? "✓ " : ""}
                    {r.title}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-mist">{r.text}</p>
                </div>
              ))}
              <p className="text-xs leading-relaxed text-mist/70">
                Resultado apenas indicativo, com base nas regras gerais e nas informações informadas. Regras de
                transição, períodos especiais, deficiência, períodos de professor e o seu CNIS podem mudar o
                resultado — inclusive para melhor.
              </p>
              <button
                type="button"
                data-testid="rc-talk"
                onClick={talk}
                className="inline-flex items-center gap-2 rounded-full border border-gold-500/60 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-gold-400 transition-colors hover:bg-gold-500 hover:text-navy-950"
              >
                <MessageCircle className="h-4 w-4" /> Analisar meu caso
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
